import React from 'react';
import MediumPosts from './MediumPosts';
import { publications } from '../data/publications';
import { useScholarMetrics } from '../hooks/useScholarMetrics';
import 'font-awesome/css/font-awesome.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faQuoteLeft, faEnvelope } from '@fortawesome/free-solid-svg-icons';

// Research experience start date (day-level calculation)
const RESEARCH_START_DATE = new Date('2024-01-01T00:00:00Z');
const FALLBACK_CITATIONS = 40;

function About() {
  // Dynamic metrics
  const publicationCount = publications.length;
  const yearsOfExperience = (
    (Date.now() - RESEARCH_START_DATE.getTime()) /
    (1000 * 60 * 60 * 24 * 365.2425)
  ).toFixed(1);
  const { citations: citationCount } = useScholarMetrics(FALLBACK_CITATIONS);

  return (
    <section id="about" className="about-mf sect-pt4 route">
      <div className="max-w-5xl mx-auto px-6">
        <div className="row">
          <div className="col-sm-12">
            <div className="box-shadow-full">

        {/* About Layout */}
        <div className="space-y-10 lg:space-y-12">
          <div className="title-box text-center section-header-reveal">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-text-primary">
              Biography
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full"></div>
            <div className="text-primary font-medium text-lg">
              Understanding How Machines Reason, Not Just What They Predict
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-6 text-justify">
              <p className="font-serif text-[1.24rem] leading-[1.72] text-text-primary">
                I am <strong>Mohammad Sayem Chowdhury</strong>, a computer science researcher working at the intersection of intelligent systems, interpretability, and high-stakes machine learning. My research focuses on building deep learning models whose decisions can be examined, reasoned about, and trusted, particularly in domains where errors carry real-world consequences.
              </p>

              <p className="font-serif text-[1.2rem] leading-[1.74] text-text-secondary">
                My academic training in Intelligent Systems led me early toward problems where accuracy alone was insufficient. During my graduate research, exposure to explainable AI techniques marked a turning point in how I approached machine learning. Rather than treating models as black boxes, I became interested in understanding <em>how</em> and <em>why</em> a model arrives at a decision, and whether that reasoning aligns with domain knowledge and human intuition.
              </p>

              <p className="font-serif text-[1.2rem] leading-[1.74] text-text-secondary">
                Much of my work addresses interpretability under practical constraints, including limited data availability, domain-specific complexity, and the need for explanation-driven validation. I have applied these principles across medical image analysis, plant disease diagnosis, fire and threat detection systems, and multilingual natural language processing.
              </p>
            </div>

            <aside className="lg:col-span-5 xl:col-span-4">
              <div className="bg-bg-card border border-border-color rounded-lg overflow-hidden shadow-md">
                <div className="relative overflow-hidden aspect-[4/5] border-b border-border-color">
                  <img
                    src="assets/images/profile.webp"
                    alt="Mohammad Sayem Chowdhury"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
                  <div className="grid grid-cols-3 gap-1 sm:gap-2 text-center text-text-secondary font-serif">
                    <div>
                      <div className="text-2xl sm:text-3xl font-bold text-text-primary leading-none">{publicationCount}</div>
                      <div className="text-[10px] sm:text-xs uppercase tracking-[0.08em] sm:tracking-[0.12em] mt-1">Pubs</div>
                    </div>
                    <div className="border-x border-border-color">
                      <div className="text-2xl sm:text-3xl font-bold text-text-primary leading-none">{citationCount}</div>
                      <div className="text-[10px] sm:text-xs uppercase tracking-[0.08em] sm:tracking-[0.12em] mt-1">Citations</div>
                    </div>
                    <div>
                      <div className="text-2xl sm:text-3xl font-bold text-text-primary leading-none">{yearsOfExperience}</div>
                      <div className="text-[10px] sm:text-xs uppercase tracking-[0.08em] sm:tracking-[0.12em] mt-1">Years</div>
                    </div>
                  </div>

                  <div className="flex justify-center gap-5 pt-4 border-t border-border-color">
                    <a href="https://scholar.google.com/citations?user=wDPGD9MAAAAJ&hl=en&authuser=1" target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-primary transition-colors" aria-label="Google Scholar">
                      <img src="assets/images/GoogleScholar.svg" alt="Google Scholar" className="w-6 h-6 opacity-75 hover:opacity-100 transition-opacity" />
                    </a>
                    <a href="https://github.com/saayeeem" target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-text-primary transition-colors" aria-label="GitHub">
                      <FontAwesomeIcon icon={faGithub} className="text-2xl" />
                    </a>
                    <a href="https://www.linkedin.com/in/mohammad-sayem-chowdhury-a3384b80" target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-primary transition-colors" aria-label="LinkedIn">
                      <FontAwesomeIcon icon={faLinkedin} className="text-2xl" />
                    </a>
                    <a href="mailto:m.sayem.c@gmail.com" className="text-text-muted hover:text-red-600 transition-colors" aria-label="Email">
                      <FontAwesomeIcon icon={faEnvelope} className="text-2xl" />
                    </a>
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <div className="space-y-6 text-justify">
            <p className="font-serif text-[1.2rem] leading-[1.74] text-text-secondary">
              Currently, I work as a Research Assistant at the <strong className="text-text-primary">Advanced Machine Intelligence Research Lab</strong>, where my interests extend toward explainable deep learning, cognitively inspired representations, and early explorations of neuro-symbolic methods. I am particularly interested in how models can move beyond surface-level correlations toward more structured, human-aligned reasoning.
            </p>

            <p className="font-serif text-[1.2rem] leading-[1.74] text-text-secondary">
              Outside of formal research, I engage with photography, visual art, and strategy-based games. These activities shape how I think about perception, abstraction, and decision-making, reinforcing my belief that intelligence, artificial or human, is deeply tied to how information is represented, constrained, and interpreted.
            </p>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-6 md:p-7 border-l-4 border-indigo-500 italic text-slate-700 dark:text-slate-300 relative quote-box rounded-r">
              <FontAwesomeIcon icon={faQuoteLeft} className="absolute top-4 left-4 text-indigo-200 dark:text-indigo-900 text-3xl -z-10" />
              <p className="m-0 font-serif text-lg leading-relaxed">
                At a broader level, my long-term goal is to contribute to the development of machine intelligence that is not only powerful, but understandable, systems that support human decision-making rather than obscure it.
              </p>
            </div>
          </div>
        </div>

        {/* Medium Posts Section */}
        <div className="mt-20 pt-12 border-t border-slate-200 dark:border-slate-800">
          <MediumPosts />
        </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
